import BankOperationDetails from './BankOperationDetails';
import TransactionActionSubmit from './TransactionActionSubmit';
import { CurrencyCode } from 'further-types/lp/vehicle';
import BankOperationActions, {
  BankOperationActionsMode,
} from './BankOperationActions';
import { ActionOperationType, ActionRef } from 'further-types/lp/action';
import { CalendarDay } from 'further-ui/utils';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import BankTransferTransactions from './BankTransferTransactions';
import { bankReconciliation as rules } from 'further-ui/validations';
import ErrorBoundary from 'components/ErrorBoundary';
import { Row, Column } from 'further-ui/components/Layout';

const formSchema = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal(BankOperationActionsMode.CreateTransfer),
    reference: z.string().optional(),
    targetBankAccountId: z.string(),
    transactions: rules.CreateBankAccountTransferInput.shape.transactions,
    currencyRate: rules.CreateBankAccountTransferInput.shape.currencyRate,
  }),
  z.object({
    mode: z.literal(BankOperationActionsMode.QuickReconcile),
    transactionId: z.string(),
  }),
]);

export type FormValues = z.infer<typeof formSchema>;

type Props = {
  bankAccount: {
    vehicleId: string;
    id: string;
  };
  id: string;
  description: string;
  counterparty: string;
  date: CalendarDay;
  spent?: number;
  received?: number;
  accountCurrency: CurrencyCode;
  reconcilableTransactions: Array<{
    id: string;
    transactionAmountTcy: number;
    unreconciledAmountTcy: number;
    counterpartyLabel: string;
    actionRef: ActionRef;
    currencyCode: CurrencyCode;
    operationType: ActionOperationType;
    ledgerDate: CalendarDay;
  }>;
  onFindAndMatch: (transactionId: string, actionId?: string) => void;
};

const BankOperation: React.FC<Props> = ({
  id,
  bankAccount,
  spent,
  received,
  reconcilableTransactions,
  accountCurrency,
  description,
  counterparty,
  date,
  onFindAndMatch,
}) => {
  const bankOperationAmountTcy = (spent ? spent : received) ?? 0;

  const matchingReconcilableTransactions = reconcilableTransactions.filter(
    (transaction) =>
      transaction.unreconciledAmountTcy === Math.abs(bankOperationAmountTcy) &&
      ((transaction.operationType === ActionOperationType.Spent && spent) ||
        (transaction.operationType === ActionOperationType.Received &&
          received)) &&
      transaction.currencyCode === accountCurrency &&
      transaction.actionRef !== ActionRef.BankAccountTransfer,
  );

  const formMethods = useForm<FormValues>({
    shouldFocusError: true,
    resolver: zodResolver(formSchema),
    defaultValues: {
      mode: BankOperationActionsMode.QuickReconcile,
      transactionId: matchingReconcilableTransactions[0]?.id ?? '',
    },
    criteriaMode: 'all',
  });

  const mode = formMethods.watch('mode');

  return (
    <FormProvider {...formMethods}>
      <Row spacing="lg" breakpoint="md">
        <Column flex={1}>
          <BankOperationDetails
            vehicleId={bankAccount.vehicleId}
            bankAccountId={bankAccount.id}
            bankOperationId={id}
            description={description}
            counterparty={counterparty}
            date={date}
            spent={spent}
            received={received}
            accountCurrency={accountCurrency}
          />
        </Column>
        <Column justify="center">
          <Row fullWidth justify="center">
            <TransactionActionSubmit
              vehicleId={bankAccount.vehicleId}
              bankAccountId={bankAccount.id}
              bankAccountOperationId={id}
              bankOperationAmountTcy={bankOperationAmountTcy}
              matchingReconcilableTransactions={
                matchingReconcilableTransactions
              }
            />
          </Row>
        </Column>
        <Column flex={1}>
          <BankOperationActions
            isOutgoingTransaction={!!spent}
            bankAccountOperationId={id}
            matchingReconcilableTransactions={matchingReconcilableTransactions}
            onFindAndMatch={onFindAndMatch}
          />
        </Column>
      </Row>
      {mode === BankOperationActionsMode.CreateTransfer && (
        <ErrorBoundary>
          <BankTransferTransactions bankAccountOperationId={id} />
        </ErrorBoundary>
      )}
    </FormProvider>
  );
};

export default BankOperation;
